/**
 * WhatsApp · o canal adicional da porta de notificações (D-204, DS-699).
 *
 * O WhatsApp não é uma segunda central: o evento, o público, a permissão e o texto continuam
 * os do Notificador. O que este arquivo define é o que é PRÓPRIO do canal: quem aderiu
 * (telefone verificado e consentimento), a intenção de entrega registrada no banco
 * (`whatsapp_entrega`) e o texto curto que sai no aparelho. Nenhum telefone viaja em job.
 */
import { z } from 'zod'
import { uuid } from './comum.js'

/** Fila única do canal: o empurrão de uma entrega e a varredura periódica das pendentes. */
export const FILA_WHATSAPP_TRANSACIONAL = 'dv-whatsapp-transacional'

/**
 * O job carrega só a IDENTIDADE da entrega (ou o pedido de varredura): o worker relê a
 * entrega, o destino e a preferência antes de enviar. Telefone e texto nunca vão no job.
 */
export const jobDeWhatsapp = z.union([
  z.object({ entregaId: uuid }).strict(),
  z.object({ varredura: z.literal(true) }).strict(),
])
export type JobDeWhatsapp = z.infer<typeof jobDeWhatsapp>

/** O formato que o WAHA recebe (o worker monta o `chatId` a partir dele). */
export const telefoneE164 = z.string().regex(/^\+[1-9]\d{7,14}$/, 'O telefone do WhatsApp deve estar em E.164.')

/**
 * O telefone do Meu perfil (`(51) 99999-8888`, o `telefoneBr`) em E.164 (`+5551999998888`).
 * Só números brasileiros, que é o que o perfil aceita: DDD de 11 a 99 e assinante de 8
 * dígitos, ou de 9 começando por 9 (celular). Qualquer outra forma devolve `null`: o canal
 * fica indisponível para a pessoa, sem adivinhar o número.
 */
export function telefoneE164DoPerfil(telefone: string | null | undefined): string | null {
  if (telefone === null || telefone === undefined) return null
  const casado = /^\((\d{2})\) (\d{4,5})-(\d{4})$/.exec(telefone.trim())
  if (casado === null) return null
  const [, ddd = '', inicio = '', fim = ''] = casado
  if (!/^[1-9][1-9]$/.test(ddd)) return null
  const assinante = `${inicio}${fim}`
  if (assinante.length === 9 && !assinante.startsWith('9')) return null
  return `+55${ddd}${assinante}`
}

/** Os quatro últimos dígitos, para a tela dizer "termina em 8888" sem repetir o número. */
export function finalDoTelefone(e164: string): string {
  return e164.slice(-4)
}

/**
 * O interruptor do canal (`DV_WAHA_ATIVO`), com a regra do worker: ausente, vazio ou `false`
 * desliga; `true` (qualquer caixa) liga; outro valor é erro de configuração.
 */
export function canalWhatsappAtivo(valor: string | undefined): boolean {
  const v = valor?.trim().toLocaleLowerCase('en-US')
  if (v === undefined || v === '' || v === 'false') return false
  if (v === 'true') return true
  throw new Error('DV_WAHA_ATIVO deve ser true ou false.')
}

/**
 * A liberação da ativação gradual (`DV_WHATSAPP_LIBERADOS`): a lista de `usuario_interno.id`
 * que podem aderir e receber; `*` libera todos (só por decisão do responsável). Ausente ou
 * vazia: ninguém. É a segunda chave do canal, além do interruptor, e vale nos dois lados
 * (a API não registra e o worker não entrega para quem não está liberado).
 */
export function whatsappLiberadoPara(lista: string | undefined, usuarioId: string): boolean {
  const itens = (lista ?? '').split(',').map((x) => x.trim().toLowerCase()).filter((x) => x !== '')
  return itens.includes('*') || itens.includes(usuarioId.toLowerCase())
}

/** A versão do texto do consentimento: muda o texto, muda a versão, e a adesão antiga fica registrada como era. */
export const VERSAO_DO_CONSENTIMENTO_WHATSAPP = 'whatsapp-v1-2026-10-26'

export const TEXTO_DO_CONSENTIMENTO_WHATSAPP =
  'Autorizo o DigitalView a me enviar, por WhatsApp e neste número, as notificações das categorias que eu ligar '
  + 'nas minhas preferências. Posso revogar a autorização a qualquer momento nesta tela.'

/**
 * Os limites técnicos do canal (D-204 (7)): conservadores, contra abuso e rajada, sem regra
 * comercial de horário ou prioridade. Mudá-los é decisão registrada, não ajuste fino.
 */
export const LIMITES_DO_WHATSAPP = {
  /** Mensagens aceitas por pessoa numa hora; acima disso a entrega é descartada com motivo. */
  porPessoaPorHora: 20,
  /**
   * Menções aceitas por pessoa numa hora, dentro das 20: quem é mencionado em rajada não perde, por
   * cota esgotada, o aviso sério (a aprovação, o Sicoob) que vem depois.
   */
  mencoesPorPessoaPorHora: 5,
  /** Mensagens aceitas pelo canal num minuto; acima disso a entrega espera a próxima varredura. */
  porMinuto: 10,
  /** O intervalo mínimo entre dois envios do mesmo worker. */
  intervaloMinimoMs: 3_000,
  /** A validade do aviso unitário: depois disso ele é obsoleto e não sai. */
  validadeDoAvisoHoras: 6,
  /** A validade do resumo do dia. */
  validadeDoResumoHoras: 12,
  /** A validade do código de verificação. */
  validadeDoCodigoMinutos: 10,
  /** Erros de digitação aceitos por código. */
  tentativasDeCodigo: 5,
  /** Códigos enviados por pessoa numa hora. */
  codigosPorHora: 3,
  /** Envios que não chegaram ao WAHA (conexão recusada) antes de desistir. */
  tentativasDeEnvio: 3,
  /** Entrega presa em `enviando` além disto: o worker caiu no meio, e ela vira indeterminada. */
  enviandoMaximoMinutos: 2,
  /** Itens por varredura. */
  porVarredura: 10,
} as const

/**
 * Os estados da intenção de entrega (`whatsapp_entrega.estado`).
 *  - `pendente`: registrada, ainda não saiu;
 *  - `enviando`: o worker reservou e está chamando o WAHA;
 *  - `aceita`: o WAHA respondeu com o id da mensagem (aceite, não leitura);
 *  - `indeterminada`: a chamada pode ter saído e a resposta se perdeu; NUNCA se repete sozinha;
 *  - `falhou`: o WAHA recusou, ou o envio não chegou a sair depois das tentativas;
 *  - `descartada`: a condição mudou antes do envio (revogação, telefone, preferência, limite);
 *  - `expirada`: o aviso ficou obsoleto antes de sair.
 */
export const ESTADOS_DA_ENTREGA_WHATSAPP = [
  'pendente', 'enviando', 'aceita', 'indeterminada', 'falhou', 'descartada', 'expirada',
] as const
export const estadoDaEntregaWhatsapp = z.enum(ESTADOS_DA_ENTREGA_WHATSAPP)
export type EstadoDaEntregaWhatsapp = z.infer<typeof estadoDaEntregaWhatsapp>

/** Os eventos do canal que não são do catálogo: o código de verificação e a demonstração autorizada. */
export const EVENTO_DE_VERIFICACAO_WHATSAPP = 'whatsapp_verificacao'
export const EVENTO_DE_DEMONSTRACAO_WHATSAPP = 'whatsapp_demonstracao'

/**
 * Onde a pessoa está no caminho de adesão, para a 2.3 dizer a coisa certa (sem misturar
 * canal desligado, liberação, telefone e consentimento):
 *  - `canal_indisponivel`: o canal está desligado no servidor;
 *  - `nao_liberado`: ligado, mas a ativação gradual ainda não inclui esta pessoa;
 *  - `sem_telefone`: falta o celular no Meu perfil (ou ele não é um número brasileiro válido);
 *  - `aguardando_verificacao`: há telefone, falta pedir o código;
 *  - `codigo_enviado`: o código foi pedido e ainda vale;
 *  - `ativo`: telefone verificado e consentimento válido;
 *  - `revogado`: a pessoa revogou; pode aderir de novo.
 */
export const SITUACOES_DO_WHATSAPP = [
  'canal_indisponivel', 'nao_liberado', 'sem_telefone', 'aguardando_verificacao', 'codigo_enviado', 'ativo', 'revogado',
] as const
export const situacaoDoWhatsapp = z.enum(SITUACOES_DO_WHATSAPP)
export type SituacaoDoWhatsapp = z.infer<typeof situacaoDoWhatsapp>

export const estadoDoWhatsapp = z.object({
  situacao: situacaoDoWhatsapp,
  /** Os quatro últimos dígitos do número que vale (o do perfil, ou o verificado). Nunca o número inteiro. */
  finalDoTelefone: z.string().regex(/^\d{4}$/).nullable(),
  codigoExpiraEm: z.string().nullable(),
  verificadoEm: z.string().nullable(),
  consentidoEm: z.string().nullable(),
  textoDoConsentimento: z.string(),
  versaoDoConsentimento: z.string(),
})
export type EstadoDoWhatsapp = z.infer<typeof estadoDoWhatsapp>

export const entradaDeVerificacaoWhatsapp = z.object({
  codigo: z.string().regex(/^\d{6}$/, 'O código tem 6 dígitos.'),
  consentimento: z.literal(true, { error: 'Marque a autorização para receber notificações pelo WhatsApp.' }),
  versaoDoConsentimento: z.literal(VERSAO_DO_CONSENTIMENTO_WHATSAPP, {
    error: 'O texto da autorização mudou. Recarregue a página e leia de novo.',
  }),
})
export type EntradaDeVerificacaoWhatsapp = z.infer<typeof entradaDeVerificacaoWhatsapp>

/** Os tamanhos da mensagem no aparelho: curta, sem texto longo copiado do e-mail. */
export const TAMANHOS_DA_MENSAGEM_WHATSAPP = { titulo: 150, corpo: 500, total: 1_000 } as const

function encurtar(texto: string, maximo: number): string {
  const limpo = texto.replace(/\s+/g, ' ').trim()
  return limpo.length <= maximo ? limpo : `${limpo.slice(0, Math.max(0, maximo - 1)).trimEnd()}…`
}

/** Uma sequência com cara de telefone (o WhatsApp a torna tocável): dígitos, espaço, hífen e parênteses. */
const PARECE_TELEFONE = /\+?\(?\d[\d\s()-]{6,}\d/g

/**
 * O texto livre (título, nome, corpo) no aparelho, para que ninguém do sistema ponha um link ou um
 * telefone tocável sob o número oficial (o único link da mensagem é o do DigitalView, à parte):
 *  - sem caracteres invisíveis de formatação (`\p{Cf}`, que esconderiam um endereço da regra abaixo);
 *  - sem a marcação do WhatsApp (`*`, `_`, `~` e crase, que imitariam o cabeçalho em negrito);
 *  - todo endereço desarmado: o esquema vira `[:]//` e o ponto antes de duas letras vira `[.]`
 *    (`exemplo[.]com`, também no domínio internacionalizado; "S.A." fica como está);
 *  - a sequência de 8 ou mais dígitos com cara de telefone vira "[número omitido]" (data e valor,
 *    com barra, ponto e vírgula, não casam).
 * Tudo linear: quem chama já cortou o texto no tamanho do canal.
 */
export function textoSeguroParaWhatsapp(texto: string): string {
  return texto
    .replace(/\p{Cf}/gu, '')
    .replace(/[*_~`]/g, '')
    .replace(/:\/\//g, '[:]//')
    .replace(/\.(?=\p{L}{2})/gu, '[.]')
    .replace(PARECE_TELEFONE, (trecho) => (trecho.replace(/\D/g, '').length >= 8 ? '[número omitido]' : trecho))
}

/** Corta, desarma e corta de novo: a regra nunca roda sobre texto maior que o canal, e o desarme não estoura o tamanho. */
function textoDoCanal(texto: string, maximo: number): string {
  return encurtar(textoSeguroParaWhatsapp(encurtar(texto, maximo)), maximo)
}

export interface PartesDaMensagemWhatsapp {
  readonly titulo: string
  readonly corpo: string
  /** Link absoluto do app (exige login; nunca carrega credencial), ou nulo. */
  readonly link: string | null
  /** Demonstração autorizada: o cabeçalho e o rodapé dizem que nada aconteceu. */
  readonly demonstracao?: { readonly numero: number; readonly total: number }
}

/**
 * O texto que sai no aparelho: a identificação discreta, o título em negrito, uma frase
 * útil e o link. Sem ids técnicos e sem nada que o texto da Inbox (já minimizado por
 * destinatário) não tenha.
 */
export function formatarMensagemWhatsapp(p: PartesDaMensagemWhatsapp): string {
  const cabecalho = p.demonstracao === undefined
    ? 'DigitalView'
    : `[TESTE DIGITALVIEW — ${String(p.demonstracao.numero).padStart(2, '0')}/${String(p.demonstracao.total)}]`
  const linhas = [`*${cabecalho}*`, `*${textoDoCanal(p.titulo, TAMANHOS_DA_MENSAGEM_WHATSAPP.titulo)}*`]
  const corpo = textoDoCanal(p.corpo, TAMANHOS_DA_MENSAGEM_WHATSAPP.corpo)
  if (corpo !== '') linhas.push(corpo)
  if (p.link !== null) linhas.push(p.link)
  if (p.demonstracao !== undefined) linhas.push('_Mensagem de demonstração. Nenhuma operação real foi executada._')
  const texto = linhas.join('\n')
  return texto.length <= TAMANHOS_DA_MENSAGEM_WHATSAPP.total
    ? texto
    : `${texto.slice(0, TAMANHOS_DA_MENSAGEM_WHATSAPP.total - 1)}…`
}

/** A mensagem do código de verificação. */
export function mensagemDeVerificacaoWhatsapp(codigo: string): string {
  return [
    '*DigitalView*',
    `Seu código para receber notificações pelo WhatsApp: *${codigo}*`,
    `Ele vale por ${String(LIMITES_DO_WHATSAPP.validadeDoCodigoMinutos)} minutos. Se você não pediu, ignore esta mensagem.`,
  ].join('\n')
}
